import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import type {
  MusteriKayit,
  MusteriKayitDurum,
  MusteriTeslimat,
} from "../../mocks/musteriPaneli";
import type { CustomerItem } from "../../mocks/customers";
import type { AnimalItem, ShareItem } from "../../mocks/animals";
import { useAnimals } from "../../hooks/useAnimals";
import { useCustomers } from "../../hooks/useCustomers";
import { usePayments } from "../../hooks/usePayments";
import { useUsers } from "../../hooks/useUsers";
import { getShareTotal, isShareOwnedByCustomer } from "../../lib/cascadeOps";
import {
  getDeliveryAssignments,
  subscribeDeliveryAssignments,
  type DeliveryAssignment,
} from "../../lib/deliveryStore";
import {
  getSlaughterPlanRecords,
  subscribeSlaughterPlanRecords,
  type SlaughterPlanRecord,
} from "../../lib/slaughterPlanStore";
import {
  deriveSlaughterOpStatus,
  getSlaughterOps,
  subscribeSlaughterOps,
  type SlaughterOpStatus,
} from "../../lib/slaughterOpsStore";
import {
  clearSessionUser,
  mustForcePasswordChange,
  readSessionUser,
  restoreSessionFromApi,
  type SessionUser,
} from "../../lib/authSession";
import ShareCard from "./components/ShareCard";
import { usePublicSiteContent } from "../../hooks/useSiteContent";
import { telHref } from "../../lib/siteContentStore";

const fmt = (n: number) =>
  n.toLocaleString("tr-TR", {
    style: "currency",
    currency: "TRY",
    maximumFractionDigits: 0,
  });

function formatTime(value?: string) {
  if (!value) return undefined;

  try {
    const date = new Date(value);
    return date.toLocaleTimeString("tr-TR", {
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return undefined;
  }
}

function resolveCustomerStatus(
  animal: AnimalItem,
  opStatus?: SlaughterOpStatus,
  delivery?: DeliveryAssignment
): MusteriKayitDurum {
  if (delivery?.status === "Teslim Edildi") return "Teslim Edildi";
  if (delivery?.status === "Taşıma Halinde") return "Taşıma Halinde";
  if (delivery?.status === "Hazır") return "Hazır";

  if (opStatus === "done") return "Hazır";
  if (opStatus === "queued") return "Kesim Bekliyor";
  if (opStatus === "entered") return "Kesime Girdi";
  if (opStatus === "cut") return "Kesim Yapıldı";
  if (opStatus === "packing") return "Parçalanıyor";

  if (animal.tamamlandi) return "Tamamlandı";
  if (animal.durum === "Teslim Edildi") return "Tamamlandı";
  if (animal.durum === "Kesim Bekliyor") return "Kesim Bekliyor";
  if (animal.durum === "Kesildi") return "Hazır";

  return "Aktif";
}

function mapDeliveryToCustomerView(
  delivery: DeliveryAssignment | undefined,
  animal: AnimalItem,
  share: ShareItem,
  users: ReturnType<typeof useUsers>["users"]
): MusteriTeslimat | undefined {
  if (!delivery) return undefined;

  const carrier = users.find((user) => user.id === delivery.carrierUserId);

  return {
    tasiyiciAdi: carrier?.adSoyad || "Taşıyıcı atanmadı",
    tasiyiciTelefon: carrier?.telefon || "",
    durum:
      delivery.status === "Taşıma Halinde"
        ? "Taşıma Halinde"
        : delivery.status === "Teslim Edildi"
          ? "Teslim Edildi"
          : "Hazır",
    hayvanKupeNo: animal.kupeNo,
    hisseNo: share.hisseNo,
    adres: delivery.address ?? "",
    canliKonumLinki: delivery.liveLocationUrl ?? "",
    teslimSaati: formatTime(delivery.deliveredAt),
    not: delivery.note ?? "",
  };
}

function mapShareToRecord(
  animal: AnimalItem,
  share: ShareItem,
  payments: ReturnType<typeof usePayments>["payments"],
  users: ReturnType<typeof useUsers>["users"],
  planRecord?: SlaughterPlanRecord,
  opStatus?: SlaughterOpStatus,
  delivery?: DeliveryAssignment
): MusteriKayit {
  const hasSingleShare = (animal.hisseler?.length ?? 0) === 1;

  const recordPayments = (payments ?? [])
    .filter((payment) => {
      if (payment.shareId === share.id) return true;
      if (!payment.shareId && hasSingleShare && payment.animalId === animal.id) return true;
      return false;
    })
    .sort((a, b) => a.tarih.localeCompare(b.tarih))
    .map((payment) => ({
      tarih: payment.tarih,
      miktar: payment.tutar,
      aciklama: payment.aciklama || payment.tip,
    }));

  const toplamTutar = getShareTotal(share);
  const odenen =
    share.odenen ?? recordPayments.reduce((sum, payment) => sum + payment.miktar, 0);
  const kalan = Math.max(0, toplamTutar - odenen);
  const teslimat = mapDeliveryToCustomerView(delivery, animal, share, users);

  return {
    id: share.id,
    hayvanKupeNo: animal.kupeNo,
    hayvanTur: animal.tur,
    hayvanKg: animal.kg ?? 0,
    hisseNo: share.hisseNo,
    hisseSayisi: share.hisseSayisi,
    etTutari: share.tutar,
    kesimUcreti: share.kesimUcreti ?? 0,
    parcalamaUcreti: share.parcalamaUcreti ?? 0,
    tasimaUcreti: share.tasimaUcreti ?? 0,
    indirim: share.indirim ?? 0,
    toplamTutar,
    odenen,
    kalan,
    durum: resolveCustomerStatus(animal, opStatus, delivery),
    kesimTarihi: planRecord?.date,
    kesimSaati: planRecord?.time,
    tamamlanmaTarihi:
      delivery?.deliveredAt ?? (animal.tamamlandi ? planRecord?.date : undefined),
    teslimat,
    odemeler: recordPayments,
  };
}

export default function MusteriPaneliPage() {
  const navigate = useNavigate();
  const { users } = useUsers();
  const { customers } = useCustomers();
  const { animals } = useAnimals();
  const { payments } = usePayments();
  const siteContent = usePublicSiteContent();

  const [showGecmis, setShowGecmis] = useState(false);
  const [authReady, setAuthReady] = useState(false);
  const [sessionUser, setSessionUser] = useState<SessionUser | null>(() =>
    readSessionUser()
  );
  const [version, setVersion] = useState(0);

  useEffect(() => {
    let active = true;

    async function boot() {
      const localUser = readSessionUser();

      if (!localUser) {
        clearSessionUser();
        if (active) {
          navigate("/giris", { replace: true });
        }
        return;
      }

      if (active) {
        setSessionUser(localUser);
      }

      const restored = await restoreSessionFromApi();

      if (!active) return;

      if (!restored) {
        navigate("/giris", { replace: true });
        return;
      }

      if (restored.rol !== "Müşteri") {
        navigate("/panel/hayvanlar", { replace: true });
        return;
      }

      setSessionUser(restored);
      setAuthReady(true);
    }

    void boot();

    return () => {
      active = false;
    };
  }, [navigate]);

  useEffect(() => {
    const unsubscribePlan = subscribeSlaughterPlanRecords(() =>
      setVersion((value) => value + 1)
    );
    const unsubscribeOps = subscribeSlaughterOps(() =>
      setVersion((value) => value + 1)
    );
    const unsubscribeDelivery = subscribeDeliveryAssignments(() =>
      setVersion((value) => value + 1)
    );

    return () => {
      unsubscribePlan();
      unsubscribeOps();
      unsubscribeDelivery();
    };
  }, []);

  const currentUser = useMemo(() => {
    if (!sessionUser) return null;
    return users.find((user) => user.id === sessionUser.id) ?? null;
  }, [sessionUser, users]);

  useEffect(() => {
    if (!authReady || !sessionUser) return;

    const effectiveUser = currentUser ?? sessionUser;

    if ("durum" in effectiveUser && effectiveUser.durum !== "Aktif") {
      clearSessionUser();
      navigate("/giris", { replace: true });
      return;
    }

    if (mustForcePasswordChange(effectiveUser)) {
      navigate("/sifre-degistir", { replace: true });
    }
  }, [authReady, sessionUser, currentUser, navigate]);

const currentCustomer = useMemo<CustomerItem | null>(() => {
  const customerId = currentUser?.customerId ?? sessionUser?.customerId;

  if (typeof customerId === "number") {
    return customers.find((customer) => customer.id === customerId) ?? null;
  }

  if (!currentUser) {
    return null;
  }

  return (
    customers.find(
      (customer) =>
        customer.telefon.replace(/\s/g, "") ===
          currentUser.telefon.replace(/\s/g, "") ||
        customer.adSoyad.trim().toLowerCase() ===
          currentUser.adSoyad.trim().toLowerCase()
    ) ?? null
  );
}, [sessionUser, currentUser, customers]);

  const planMap = useMemo(
    () => new Map(getSlaughterPlanRecords().map((record) => [record.animalId, record])),
    [version]
  );

  const opStatusMap = useMemo(() => {
    const rawOps = getSlaughterOps();
    const rawMap = new Map(rawOps.map((record) => [record.animalId, record.status]));
    const next = new Map<number, SlaughterOpStatus>();

    for (const animal of animals) {
      next.set(animal.id, deriveSlaughterOpStatus(animal, rawMap));
    }

    return next;
  }, [animals, version]);

  const deliveryMap = useMemo(() => {
    const next = new Map<number, DeliveryAssignment>();

    for (const item of getDeliveryAssignments()) {
      next.set(item.shareId, item);
    }

    return next;
  }, [version]);

  const allRecords = useMemo<MusteriKayit[]>(() => {
    if (!currentCustomer) return [];

    return animals
      .flatMap((animal) =>
        animal.hisseler
          .filter((share) => isShareOwnedByCustomer(share, currentCustomer))
          .map((share) =>
            mapShareToRecord(
              animal,
              share,
              payments,
              users,
              planMap.get(animal.id),
              opStatusMap.get(animal.id),
              deliveryMap.get(share.id)
            )
          )
      )
      .sort((a, b) => a.hayvanKupeNo.localeCompare(b.hayvanKupeNo, "tr"));
  }, [animals, payments, currentCustomer, users, planMap, opStatusMap, deliveryMap]);

  const aktifKayitlar = useMemo(
    () =>
      allRecords.filter(
        (record) => record.durum !== "Tamamlandı" && record.durum !== "Teslim Edildi"
      ),
    [allRecords]
  );

  const tamamlananKayitlar = useMemo(
    () =>
      allRecords.filter(
        (record) => record.durum === "Tamamlandı" || record.durum === "Teslim Edildi"
      ),
    [allRecords]
  );

  const toplamTutar = allRecords.reduce((sum, record) => sum + record.toplamTutar, 0);
  const toplamOdenen = allRecords.reduce((sum, record) => sum + record.odenen, 0);
  const toplamKalan = allRecords.reduce((sum, record) => sum + record.kalan, 0);
  const pct = toplamTutar > 0 ? Math.round((toplamOdenen / toplamTutar) * 100) : 100;

  if (!authReady || !sessionUser) {
    return null;
  }

  const effectiveUser = currentUser ?? sessionUser;

  if (effectiveUser.rol !== "Müşteri") {
    return null;
  }

  if (!currentCustomer) {
    if (customers.length === 0) {
      return null;
    }

    return (
      <div className="min-h-screen bg-farm-cream font-body">
        <div className="sticky top-0 z-40 bg-farm-warm border-b border-farm-border">
          <div className="max-w-lg mx-auto px-4 h-12 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 flex items-center justify-center rounded-full overflow-hidden bg-farm-earth-light flex-shrink-0">
                <img
                  src="https://public.readdy.ai/ai/img_res/73cf5855-ea32-42f4-956b-881be407de25.png"
                  alt="Evren Çiftliği"
                  className="w-6 h-6 object-contain"
                />
              </div>
              <span className="font-heading font-semibold text-sm text-farm-brown">
                Evren Çiftliği
              </span>
            </div>
          </div>
        </div>

        <div className="max-w-lg mx-auto px-4 py-6">
          <div className="bg-farm-warm border border-farm-border rounded-farm p-5 text-center">
            <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full bg-farm-earth-pale text-farm-earth">
              <i className="ri-user-search-line text-2xl" />
            </div>
            <p className="text-sm font-semibold text-farm-brown">
              Bu hesaba bağlı müşteri kaydı bulunamadı
            </p>
            <p className="text-xs text-farm-muted mt-1">
              Admin panelinden bu kullanıcıya müşteri kaydı bağlanmalı.
            </p>
            <button
              onClick={() => {
                clearSessionUser();
                navigate("/giris");
              }}
              className="mt-4 px-4 py-2 rounded-full bg-farm-earth text-white text-xs font-semibold hover:bg-farm-brown transition-colors"
            >
              Çıkış Yap
            </button>
          </div>
        </div>
      </div>
    );
  }

  const firstName = currentCustomer.adSoyad.split(" ")[0];

  return (
    <div className="min-h-screen bg-farm-cream font-body">
      <div className="sticky top-0 z-40 bg-farm-warm border-b border-farm-border">
        <div className="max-w-lg mx-auto px-4 h-12 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 flex items-center justify-center rounded-full overflow-hidden bg-farm-earth-light flex-shrink-0">
              <img
                src="https://public.readdy.ai/ai/img_res/73cf5855-ea32-42f4-956b-881be407de25.png"
                alt="Evren Çiftliği"
                className="w-6 h-6 object-contain"
              />
            </div>
            <span className="font-heading font-semibold text-sm text-farm-brown">
              Evren Çiftliği
            </span>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate("/sifre-degistir")}
              className="h-8 px-3 rounded-full border border-farm-border bg-farm-cream text-farm-brown text-[10px] font-semibold hover:border-farm-earth/40"
            >
              Şifre Değiştir
            </button>

            <div className="text-right hidden sm:block">
              <p className="text-xs font-semibold text-farm-brown leading-none">
                {currentCustomer.adSoyad}
              </p>
              <p className="text-[10px] text-farm-muted leading-none mt-0.5">
                Müşteri Paneli
              </p>
            </div>

            <div className="w-8 h-8 flex items-center justify-center rounded-full bg-farm-earth-pale text-farm-earth font-bold text-xs">
              {currentCustomer.adSoyad
                .split(" ")
                .map((part) => part[0])
                .join("")
                .toUpperCase()
                .slice(0, 2)}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 py-5 space-y-5">
        <div className="rounded-farm bg-farm-warm border border-farm-border overflow-hidden">
          <div
            className="relative px-4 py-5 flex items-center gap-4"
            style={{
              backgroundImage:
                "url(https://readdy.ai/api/search-image?query=warm%20pastoral%20countryside%20farm%20field%20with%20soft%20golden%20sunlight%20gentle%20hills%20and%20green%20grass%20minimal%20clean%20background%20soothing%20nature&width=800&height=200&seq=welcome-bg-01&orientation=landscape)",
              backgroundSize: "cover",
              backgroundPosition: "center top",
            }}
          >
            <div className="absolute inset-0 bg-gradient-to-r from-white/92 via-white/80 to-white/50" />
            <div className="relative z-10">
              <p className="font-body text-xs text-farm-muted">Hoş geldiniz</p>
              <h1 className="font-heading font-bold text-xl text-farm-brown leading-tight">
                Merhaba, {firstName}!
              </h1>
              <p className="font-body text-xs text-farm-muted mt-0.5">
                {currentCustomer.telefon}
              </p>
            </div>
          </div>
        </div>

        <div className="rounded-farm bg-farm-warm border border-farm-border px-4 py-3">
          <p className="text-[10px] font-semibold text-farm-muted uppercase tracking-wide mb-3">
            Ödeme Özeti
          </p>

          <div className="grid grid-cols-3 gap-2 mb-3">
            <div className="text-center bg-farm-cream rounded px-2 py-2.5">
              <p className="text-[10px] text-farm-muted mb-0.5">Toplam</p>
              <p className="text-sm font-bold text-farm-brown">{fmt(toplamTutar)}</p>
            </div>

            <div className="text-center bg-farm-green-pale/60 rounded px-2 py-2.5">
              <p className="text-[10px] text-farm-muted mb-0.5">Ödenen</p>
              <p className="text-sm font-bold text-farm-green">{fmt(toplamOdenen)}</p>
            </div>

            <div
              className={`text-center rounded px-2 py-2.5 ${
                toplamKalan > 0 ? "bg-farm-earth-pale/60" : "bg-farm-green-pale/40"
              }`}
            >
              <p className="text-[10px] text-farm-muted mb-0.5">Kalan</p>
              <p
                className={`text-sm font-bold ${
                  toplamKalan > 0 ? "text-farm-earth" : "text-farm-green"
                }`}
              >
                {toplamKalan > 0 ? fmt(toplamKalan) : "Tamamlandı"}
              </p>
            </div>
          </div>

          <div>
            <div className="flex justify-between text-[10px] text-farm-muted mb-1">
              <span>Ödeme tamamlanma oranı</span>
              <span className="font-semibold text-farm-brown">%{pct}</span>
            </div>
            <div className="h-2.5 bg-farm-cream rounded-full overflow-hidden border border-farm-border">
              <div
                className={`h-full rounded-full transition-all ${
                  toplamKalan > 0 ? "bg-farm-earth" : "bg-farm-green"
                }`}
                style={{ width: `${pct}%` }}
              />
            </div>
          </div>
        </div>

        <div>
          <div className="flex items-center gap-2 mb-3">
            <div className="w-6 h-6 flex items-center justify-center bg-farm-earth-pale text-farm-earth rounded">
              <i className="ri-folder-open-line text-xs" />
            </div>
            <h2 className="font-heading font-semibold text-sm text-farm-brown">
              Aktif Kayıtlarım
            </h2>
            <span className="text-[10px] text-farm-muted bg-farm-border/60 px-1.5 py-0.5 rounded-full">
              {aktifKayitlar.length}
            </span>
          </div>

          {aktifKayitlar.length === 0 ? (
            <div className="text-center py-8 bg-farm-warm border border-farm-border rounded-farm text-farm-muted text-sm">
              Aktif kaydınız bulunmuyor.
            </div>
          ) : (
            <div className="space-y-3">
              {aktifKayitlar.map((record) => (
                <ShareCard key={record.id} kayit={record} />
              ))}
            </div>
          )}
        </div>

        {tamamlananKayitlar.length > 0 && (
          <div>
            <button
              onClick={() => setShowGecmis((value) => !value)}
              className="w-full flex items-center justify-between px-4 py-3 bg-farm-warm border border-farm-border rounded-farm hover:bg-farm-cream/60 transition-colors cursor-pointer"
            >
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 flex items-center justify-center bg-farm-green-pale text-farm-green rounded">
                  <i className="ri-history-line text-xs" />
                </div>
                <span className="font-heading font-semibold text-sm text-farm-brown">
                  Geçmiş Siparişler
                </span>
                <span className="text-[10px] text-farm-muted bg-farm-border/60 px-1.5 py-0.5 rounded-full">
                  {tamamlananKayitlar.length}
                </span>
              </div>
              <i
                className={`${
                  showGecmis ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"
                } text-farm-muted text-lg`}
              />
            </button>

            {showGecmis && (
              <div className="mt-3 space-y-3">
                {tamamlananKayitlar.map((record) => (
                  <ShareCard key={record.id} kayit={record} isCompleted />
                ))}
              </div>
            )}
          </div>
        )}

        <div className="text-center pb-4 space-y-1">
          <p className="text-[10px] text-farm-muted">Sorularınız için:</p>
          <a
            href={`tel:${telHref(siteContent)}`}
            className="flex items-center justify-center gap-1.5 text-xs font-semibold text-farm-earth cursor-pointer"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-phone-line text-xs" />
            </div>
            {siteContent.contactPhone} — {siteContent.brandName}
          </a>

          <div className="pt-2">
            <button
              onClick={() => {
                clearSessionUser();
                navigate("/giris");
              }}
              className="text-[10px] text-farm-muted hover:text-farm-brown underline"
            >
              Çıkış Yap
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}